import { uniqueId } from "lodash";
import { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Translate } from "react-localize-redux";
import Checkbox from "../../../../utils/components/Checkbox";
import Input from "../../../../utils/components/Input";
import SelectBox from "../../../../utils/components/SelectBox";
import Textarea from "../../../../utils/components/Textarea";
import { formFieldInputs } from "../../../../utils/db/formFields";
import ValuesInput from "../../TemplateStructureComponents/ValuesInput";
import { getUnixTime } from "date-fns";
import timeStampToDateFormat from "../../../../utils/helpers/timestampToDateFormat";

import toastr from "toastr";

const DateCustomInput = forwardRef(
  (
    {
      value,
      onClick,
      headTitle,
      name,
      placeholder,
      disabled,
      required,
      onChange,
      isReadOnly,
    },
    ref,
  ) => (
    <div role="button" onClick={onClick} ref={ref}>
      <Input
        headTitle={headTitle}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        value={value}
        onChange={onChange}
        hasIcon={false}
        inputProps={{
          readOnly: isReadOnly,
        }}
      />
    </div>
  ),
);

const Field = ({ id, fieldType, onChange, values, ...rest }) => {
  const field = formFieldInputs[fieldType].sort((a, b) => a.order - b.order);

  return (
    <div className="SingleField">
      <Translate>
        {({ translate }) => (
          <>
            {field
              .sort((a, b) => a.order - b.order)
              .map((input) => {
                let format = "dd/MM/yyyy";
                if (input.type === "date") {
                  if (input.format.startsWith("input")) {
                    const selectedType = field.find(
                      (item) => item.name === input.format.split(":")[1],
                    );

                    if (selectedType) {
                      format = selectedType?.options.find(
                        (item) => item.value === values[selectedType?.name],
                      )?.format;
                    } else format = "dd/MM/yyyy";
                  } else format = input.format;
                }

                if (input.with) {
                  const [withName, withValue] = input.with.split(":");
                  if (values[withName] !== withValue) return false;
                }

                let filteredOptions = input.options;
                if (input.name === "type" && fieldType === "selection" && values.data_source === "from_cascading_set") {
                  filteredOptions = input.options.filter(option => option.value !== "multi_selection");
                }

                return (
                  <div key={input.name} className="mb-4 position-relative">
                    {input.type === "textarea" ? (
                      <Textarea
                        headTitle={translate(`form_field_label.${input.label}`)}
                        name={input.name}
                        placeholder={input.placeholder}
                        disabled={input.disabled}
                        required={input.required}
                        rows={4}
                        onChange={onChange}
                        value={values[input.name] || input.defaultValue || ""}
                      />
                    ) : input.type === "array.text" ? (
                      <ValuesInput
                        values={values[input.name] || input.defaultValue || []}
                        label="Values"
                        onAdd={(value, callback) => {
                          if (values[input.name].includes(value)) {
                            toastr.warning(
                              translate(
                                "form_templates.template_structure.value_already_exists",
                              ),
                            );
                            return;
                          }
                          const newValues = [...values[input.name], value];
                          onChange({
                            target: {
                              name: [input.name],
                              value: newValues,
                              type: "text",
                            },
                          });
                          callback && callback();
                        }}
                        onDelete={(index) => {
                          const newValues = [...values[input.name]];
                          newValues.splice(index, 1);
                          onChange({
                            target: {
                              name: [input.name],
                              value: newValues,
                              type: "text",
                            },
                          });
                        }}
                        onChange={(value, index) => {
                          const newValues = [...values[input.name]];
                          newValues[index] = value;
                          onChange({
                            target: {
                              name: [input.name],
                              value: newValues,
                              type: "text",
                            },
                          });
                        }}
                      />
                    ) : ["text", "number"].some((value) =>
                      input.type.includes(value),
                    ) ? (
                      <Input
                        headTitle={translate(`form_field_label.${input.label}`)}
                        type={input.type}
                        name={input.name}
                        placeholder={input.placeholder}
                        disabled={input.disabled}
                        required={input.required}
                        value={values[input.name] ?? (input.defaultValue || "")}
                        onChange={onChange}
                        hasIcon={false}
                        inputProps={{
                          readOnly: input.isReadOnly,
                        }}
                      />
                    ) : input.type === "checkbox" ? (
                      <Checkbox
                        id={uniqueId("checkbox-")}
                        label={translate(`form_field_label.${input.label}`)}
                        name={input.name}
                        checked={values[input.name] || input.defaultValue || ""}
                        onChange={(checked) =>
                          onChange({
                            target: {
                              checked,
                              name: input.name,
                              type: "checkbox",
                            },
                          })
                        }
                      />
                    ) : input.type === "selection" ? (
                      <SelectBox
                        options={
                          filteredOptions?.map(
                            ({ label, value, isStaticValue }) => ({
                              label:
                                typeof label === "string" && !isStaticValue
                                  ? translate(label)
                                  : label,
                              value,
                            }),
                          ) || []
                        }
                        value={
                          String(values[input.name])
                            ? {
                              label: filteredOptions?.find(
                                (option) =>
                                  option.value === values[input.name],
                              )
                                ? filteredOptions.find(
                                  (option) =>
                                    option.value === values[input.name],
                                ).isStaticValue
                                  ? filteredOptions.find(
                                    (option) =>
                                      option.value === values[input.name],
                                  ).label
                                  : translate(
                                    filteredOptions?.find(
                                      (option) =>
                                        option.value === values[input.name],
                                    )?.label,
                                  )
                                : "",
                              value: values[input.name],
                            }
                            : null
                        }
                        onChange={(item) =>
                          onChange({
                            target: {
                              value: item.value,
                              name: input.name,
                              type: "select",
                            },
                          })
                        }
                        customTitle={translate(
                          `form_field_label.${input.label}`,
                        )}
                      />
                    ) : input.type === "image" ? (
                      <Input
                        headTitle={translate(`form_field_label.${input.label}`)}
                        type="file"
                        name={input.name}
                        placeholder={"<<File Upload Area>>"}
                        disabled={input.disabled}
                        required={input.required}
                        onChange={onChange}
                        file={values[input.name]}
                        inputProps={{
                          readOnly: input.isReadOnly,
                        }}
                        fileType="image"
                      />
                    ) : input.type === "date" ? (
                      <DatePicker
                        selected={
                          values[input.name]
                            ? !isNaN(Number(values[input.name]))
                              ? new Date(
                                timeStampToDateFormat(
                                  values[input.name],
                                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx'",
                                ),
                              )
                              : null
                            : null
                        }
                        dateFormat={format}
                        showYearPicker={format === "yyyy"}
                        showMonthYearPicker={format === "MM/yyyy"}
                        showTimeSelect={format.includes("HH")}
                        showTimeSelectOnly={format === "HH:mm"}
                        onChange={(date) => {
                          onChange({
                            target: {
                              value: date ? getUnixTime(date) : null,
                              name: input.name,
                              type: "text",
                            },
                          });
                        }}
                        isClearable
                        customInput={
                          <DateCustomInput
                            headTitle={translate(
                              `form_field_label.${input.label}`,
                            )}
                            value={
                              values[input.name] || input.defaultValue || ""
                            }
                            name={input.name}
                            placeholder={input.placeholder}
                            disabled={input.disabled}
                            required={input.required}
                            defaultValue={input.defaultValue}
                            isReadOnly={input.isReadOnly}
                          />
                        }
                      />
                    ) : input.type === "file" ? (
                      <Input
                        headTitle={translate(`form_field_label.${input.label}`)}
                        type="file"
                        name={input.name}
                        placeholder={"<<File Upload Area>>"}
                        disabled={input.disabled}
                        required={input.required}
                        onChange={onChange}
                        file={values[input.name] || null}
                        inputProps={{
                          readOnly: input.isReadOnly,
                          "data-action": input.action || false,
                        }}
                        fileType="file"
                      />
                    ) : (
                      ""
                    )}
                  </div>
                );
              })}
            <div className="mt-4">
              <Input
                headTitle={translate("form_field_label.field_type")}
                type="text"
                name="field_type"
                value={translate(`form_fields.${fieldType}`)}
                onChange={() => false}
              />
            </div>
          </>
        )}
      </Translate>
    </div>
  );
};

export default Field;